import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  ChatBubbleLeftRightIcon,
  PlusIcon,
  XMarkIcon,
  Bars3Icon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

const makeQuestionId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * WhatsApp Agent interview setup — a lightweight question list, in the same spirit as
 * DocumentCollectionConfig: a plain controlled component, no internal persistence beyond
 * the one-shot AI generation call. The parent (JobScreeningsPage) owns persistence via
 * `questions`/`onChange`.
 */
export default function WhatsAppAgentConfig({ jobId, questions = [], onChange }) {
  const [draftQuestion, setDraftQuestion] = useState('');
  const [generating, setGenerating] = useState(false);

  const handleAddQuestion = (e) => {
    e.preventDefault();
    const text = draftQuestion.trim();
    if (!text) return;
    onChange([...questions, { id: makeQuestionId(), text }]);
    setDraftQuestion('');
  };

  const handleGenerateWithAI = async () => {
    if (!jobId) {
      toast.error('Save the job posting first so AI can use the job details.');
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post('/api/ai-interviews/recommendations', {
        jobPostingId: jobId,
        constraints: { maxQuestions: 6 },
      });
      const sections = res.data?.recommendations?.sections || {};
      const generated = [
        ...(sections.eligibility || []),
        ...(sections.technical || []),
        ...(sections.behavioral || []),
        ...(sections.cultural || []),
        ...(sections.core || []),
      ]
        .map((q) => (q?.prompt || q?.question || '').trim())
        .filter(Boolean)
        .map((text) => ({ id: makeQuestionId(), text }));

      if (generated.length === 0) {
        toast.error('No questions were generated. Please try again or add your own.');
        return;
      }
      onChange([...questions, ...generated]);
      toast.success(`Generated ${generated.length} question${generated.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to generate questions');
    } finally {
      setGenerating(false);
    }
  };

  const handleRemoveQuestion = (idToRemove) => {
    onChange(questions.filter((q) => q.id !== idToRemove));
  };

  const handleQuestionTextChange = (idToEdit, nextText) => {
    onChange(questions.map((q) => (q.id === idToEdit ? { ...q, text: nextText } : q)));
  };

  const moveQuestion = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= questions.length) return;
    const next = [...questions];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    onChange(next);
  };

  return (
    <div className="space-y-6 w-full animate-fadeIn">
      <div className="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-5 shadow-sm">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-xl bg-[#25D366]/10 text-[#25D366] shrink-0">
            <ChatBubbleLeftRightIcon className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-semibold text-gray-900">WhatsApp Interview Questions</h4>
            <p className="text-xs text-gray-600 mt-0.5">
              Add the questions the WhatsApp agent should ask candidates during the chat-based interview.
              Questions are asked in the order shown below.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleGenerateWithAI}
          disabled={generating}
          className="w-full mb-4 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-dashed border-[#007BFF]/40 bg-[#007BFF]/5 text-sm font-semibold text-[#007BFF] hover:bg-[#007BFF]/10 hover:border-[#007BFF]/60 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {generating ? (
            <>
              <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-[#007BFF]/60 border-t-transparent" />
              Generating questions…
            </>
          ) : (
            <>
              <SparklesIcon className="h-4 w-4" />
              Generate questions with AI
            </>
          )}
        </button>

        <div className="space-y-2 mb-4">
          {questions.length === 0 ? (
            <div className="text-center py-8 rounded-xl border border-dashed border-slate-200 bg-slate-50">
              <p className="text-xs sm:text-sm text-slate-500 font-medium">No questions added yet</p>
              <p className="text-[11px] text-slate-400 mt-1">Add at least one question below to continue.</p>
            </div>
          ) : (
            questions.map((q, idx) => (
              <div
                key={q.id}
                className="flex items-start gap-2.5 bg-white p-3 rounded-xl border border-slate-200/90 shadow-sm"
              >
                <div className="flex flex-col items-center gap-1 pt-1.5 shrink-0 text-slate-300">
                  <Bars3Icon className="h-4 w-4" />
                  <span className="text-[10px] font-bold text-slate-400">{idx + 1}</span>
                </div>
                <textarea
                  value={q.text}
                  onChange={(e) => handleQuestionTextChange(q.id, e.target.value)}
                  rows={2}
                  className="flex-1 min-w-0 resize-y px-3 py-2 border border-slate-200 rounded-lg text-xs sm:text-sm focus:outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/20"
                />
                <div className="flex flex-col gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => moveQuestion(idx, -1)}
                    disabled={idx === 0}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-xs px-1"
                    title="Move up"
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => moveQuestion(idx, 1)}
                    disabled={idx === questions.length - 1}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-30 disabled:cursor-not-allowed text-xs px-1"
                    title="Move down"
                  >
                    ▼
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => handleRemoveQuestion(q.id)}
                  className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0"
                  title="Remove question"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))
          )}
        </div>

        <form onSubmit={handleAddQuestion} className="flex gap-2">
          <input
            type="text"
            value={draftQuestion}
            onChange={(e) => setDraftQuestion(e.target.value)}
            placeholder="e.g. Tell us about your experience with React"
            className="flex-1 px-3.5 py-2 border border-slate-300 rounded-xl text-xs sm:text-sm focus:outline-none focus:border-[#007BFF] focus:ring-2 focus:ring-[#007BFF]/20"
          />
          <button
            type="submit"
            disabled={!draftQuestion.trim()}
            className="px-4 py-2 rounded-xl bg-[#007BFF] hover:bg-[#0062CC] text-xs sm:text-sm font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed shrink-0 flex items-center gap-1.5 shadow-sm cursor-pointer"
          >
            <PlusIcon className="h-4 w-4" />
            Add
          </button>
        </form>
      </div>
    </div>
  );
}
